import { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../../utils/api';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import { formatDate } from '../../utils/dateHelpers';

const COURSE_COLORS = {
  BLS: '#6B8063',
  ACLS: '#D4A017',
  ATLS: '#3498DB',
  NRP: '#9B59B6',
};

const COURSE_TYPES = ['BLS', 'ACLS', 'ATLS', 'NRP'];
const STATUS_MAP = { 'Planned': 'planned', 'In Progress': 'in-progress', 'Completed': 'completed' };

export default function CoursesCalendar() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [addDate, setAddDate] = useState('');

  const fetchCourses = async () => {
    try {
      const data = await api.get('/courses');
      setCourses(data.courses || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCourses(); }, []);

  const events = courses.map((c) => ({
    id: String(c.id),
    title: `${c.course_name} - ${c.instructor_name || 'TBD'}`,
    start: c.start_date,
    end: c.end_date ? new Date(new Date(c.end_date).getTime() + 86400000).toISOString().split('T')[0] : undefined,
    backgroundColor: COURSE_COLORS[c.course_name] || '#6B8063',
    borderColor: COURSE_COLORS[c.course_name] || '#6B8063',
    extendedProps: c,
  }));

  const handleEventClick = (info) => {
    setSelectedCourse(info.event.extendedProps);
  };

  const handleDateClick = (info) => {
    setAddDate(info.dateStr);
    setAddModal(true);
  };

  const handleAddCourse = async (formData) => {
    try {
      await api.post('/courses', formData);
      setAddModal(false);
      fetchCourses();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="text-sm font-medium" style={{ marginRight: '8px' }}>Legend:</span>
          {Object.entries(COURSE_COLORS).map(([name, color]) => (
            <span key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: color, display: 'inline-block' }} />
              {name}
            </span>
          ))}
        </div>
      </div>

      <div className="card">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={events}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          height="auto"
          eventDisplay="block"
        />
      </div>

      {/* Course Details Modal */}
      <Modal isOpen={!!selectedCourse} onClose={() => setSelectedCourse(null)} title="Course Details">
        {selectedCourse && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label className="text-xs text-muted">Course</label><p className="font-semibold">{selectedCourse.course_name}</p></div>
              <div><label className="text-xs text-muted">Status</label><p><Badge status={STATUS_MAP[selectedCourse.status]} text={selectedCourse.status} /></p></div>
              <div><label className="text-xs text-muted">Start Date</label><p>{formatDate(selectedCourse.start_date)}</p></div>
              <div><label className="text-xs text-muted">End Date</label><p>{formatDate(selectedCourse.end_date)}</p></div>
              <div><label className="text-xs text-muted">Location</label><p>{selectedCourse.location || 'TBD'}</p></div>
              <div><label className="text-xs text-muted">Instructor</label><p>{selectedCourse.instructor_name || 'TBD'}</p></div>
              <div><label className="text-xs text-muted">Attendees</label><p className="font-semibold">{selectedCourse.attendee_count || 0}</p></div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Course Modal */}
      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title="Add Course">
        <AddCourseForm initialDate={addDate} onSave={handleAddCourse} onCancel={() => setAddModal(false)} />
      </Modal>
    </div>
  );
}

function AddCourseForm({ initialDate, onSave, onCancel }) {
  const [form, setForm] = useState({
    course_name: 'BLS',
    start_date: initialDate || '',
    end_date: initialDate || '',
    location: '',
    instructor_name: '',
    status: 'Planned',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div className="form-row">
        <div className="form-group">
          <label>Course Type *</label>
          <select value={form.course_name} onChange={(e) => set('course_name', e.target.value)}>
            {COURSE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={form.status} onChange={(e) => set('status', e.target.value)}>
            <option value="Planned">Planned</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group"><label>Start Date *</label><input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} required /></div>
        <div className="form-group"><label>End Date *</label><input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} required /></div>
      </div>
      <div className="form-group"><label>Location</label><input value={form.location} onChange={(e) => set('location', e.target.value)} /></div>
      <div className="form-group"><label>Instructor</label><input value={form.instructor_name} onChange={(e) => set('instructor_name', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}
